import {icon} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {generateCloseButton, CLOSE_BUTTON_TYPES} from '#core/application/modules/tiles/close-button/index.js';
import {generateMiniEventItem} from '#core/application/modules/tiles/mini-event-item/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {constructTilePalette} from '#core/application/modules/tiles-cache.js';

export function quickNavigationsFactory(PALETTE) {
    const {FONT} = PALETTE;

    const NAVIGATION_CONTAINER_STYLE = {
        style: {
            padding: 0,
        },
    };
    const NAVIGATION_TITLE_CONTAINER_STYLE = {
        style: {
            ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_TITLE_CONTAINER_BG),
            margin: '0 0 4px',
        },
    };
    const NAVIGATION_TITLE_COUNTRY_ICON_STYLE = {
        icon: icon(PALETTE.QUICK_NAVIGATIONS_TITLE_ICON_COLOR),
    };
    const NAVIGATION_TITLE_STYLE = {
        style: PALETTE.QUICK_NAVIGATIONS_TITLE_TEXT,
    };
    const NAVIGATION_ITEMS_CONTAINER_STYLE = {
        separator: {
            style: {
                color: PALETTE.QUICK_NAVIGATIONS_ITEMS_DIVIDER,
            },
        },
    };
    const QUICK_NAVIGATIONS_INPLAY_MENU_ROW = generatePivotsRow(
        constructTilePalette('PIVOT', null, PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR.TOGGLE_BUTTON_BG)
    );

    return {
        QuickNavigationPane: {
            dialog: {
                closeButton: generateCloseButton(PALETTE.CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
                background: {
                    ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_CONTAINER_BG),
                },
                borderStyle: {
                    borderRadius: PALETTE.QUICK_NAVIGATIONS_CONTAINER_BORDER_RADIUS,
                },
            },
            popup: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_CONTAINER_BG),
                        borderRadius: PALETTE.QUICK_NAVIGATIONS_CONTAINER_BORDER_RADIUS,
                        boxShadow: PALETTE.QUICK_NAVIGATIONS_CONTAINER_BOX_SHADOW,
                        overflow: 'hidden',
                    },
                },
            },
        },
        QuickEventsNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            inPlayMenu: {
                rootContainer: {
                    style: {
                        _mobile_padding: 8,
                    },
                },
                navigationMenuSkeleton: {
                    linkPlaceholder: generateSkeletonPart(
                        PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR_SKELETON_PART
                    ),
                },
                navigationMenu: {
                    item: generateToggleButton(PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR, true, {
                        WITH_ACTIVE: false,
                        WITH_HOVER: false,
                    }),
                    itemIcon: icon(PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR.TOGGLE_BUTTON_ICON_COLOR),
                    selectedItemIcon: icon(
                        PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR.TOGGLE_BUTTON_SELECTED_ICON_COLOR
                    ),
                    buttonScrollPane: QUICK_NAVIGATIONS_INPLAY_MENU_ROW.buttonScrollPane,
                },
            },
            groupHeaderContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            groupHeaderLabel: NAVIGATION_TITLE_STYLE,
            eventItemsContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            eventItem: generateMiniEventItem(PALETTE.EVENTS_MINI_EVENT_ITEM),
        },
        QuickLeaguesNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            headerContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            headerLabel: NAVIGATION_TITLE_STYLE,
            leaguesContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            leagueItem: {
                button: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_BG),
                        },
                        active: {
                            ...resolveBackground(PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_BG),
                        },
                    },
                },
                buttonLabel: {
                    style: {
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonLabelActive: {
                    style: {
                        ...PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonIcon: icon(
                    PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
                buttonIconActive: icon(
                    PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
            },
        },
        QuickMeetingsNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            headerContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            headerLabel: NAVIGATION_TITLE_STYLE,
            meetingsContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            meetingItem: {
                button: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_BG),
                        },
                        active: {
                            ...resolveBackground(PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_BG),
                        },
                    },
                },
                buttonLabel: {
                    style: {
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonLabelActive: {
                    style: {
                        ...PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT
                            .MINI_EVENT_ITEM_PARTICIPANTS.PARTICIPANTS_TEXT,
                    },
                },
                buttonIcon: icon(
                    PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
                buttonIconActive: icon(
                    PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
            },
        },
    };
}
